import React, { useEffect, useState } from 'react';
import { BsThreeDotsVertical, BsPencilFill } from 'react-icons/bs';

import TruncatedDescription from '../../BayDashboard/components/TruncatedDescription';
import BayDropdown from '../../BayDropdown';

import EditWidget from './EditWidget';
import '../BayLaunchpad.scss';

interface WidgetDetails {
	id: string;
	[key: string]: unknown;
}

interface BannerConfig {
	bannerImage?: string;
	bannerHoverText?: string;
	bannerHoverUrl?: string;
	[key: string]: unknown;
}

interface DefaultWidgetStyles {
	sections?: unknown[];
	links?: unknown[];
	[key: string]: unknown;
}

interface WidgetCustomizationData {
	headerImage?: string;
	headerTitle?: string;
	headerTitleDescription?: string;
	[key: string]: unknown;
}

interface WidgetFinishStatus {
	[widgetId: string]: boolean;
}

interface EditModalInputFields {
	[widgetId: string]: unknown;
}

interface LaunchpadWidgetWrapperProps {
	widgetDetails: WidgetDetails;
	editMode?: boolean;
	headerIcon?: React.ReactNode;
	iconColor?: string;
	children?: React.ReactNode;
	isLoading?: boolean;
	t?: (key: string) => string;
	isTitleBlock?: boolean;
	wrapperClassNames?: string;
	hideHeader?: boolean;
	headerText?: string;
	headerDescription?: string;
	isBannerWidget?: boolean;
	isOtherLink?: boolean;
	editWidgetHeight?: number | null;
	isHeaderWidget?: boolean;
	setIsInPreviewMode?: (value: boolean) => void;
	setWidgetCustomizationData?: (data: WidgetCustomizationData) => void;
	setHeaderImageUrl?: (url: string) => void;
	widgetCustomizationData?: WidgetCustomizationData;
	headerWidgetImageUrl?: string;
	headerWidgetTitle?: string;
	headerWidgetDescription?: string;
	widgetFinishStatus?: WidgetFinishStatus;
	setWidgetFinishStatus?: (status: (prev: WidgetFinishStatus) => WidgetFinishStatus) => void;
	bannerConfig?: BannerConfig;
	editModalInputFields?: EditModalInputFields;
	setEditModalInputFields?: (fields: (prev: EditModalInputFields) => EditModalInputFields) => void;
	defaultWidgetStyles?: DefaultWidgetStyles;
	bannerImageUrl?: string;
	setBannerImageUrl?: (url: string) => void;
	setSnackbar?: (snackbar: { show: boolean; description: string }) => void;
	widgetDrawerThemeSettings?: Record<string, unknown>;
	selectLaunchpadSettingsData?: unknown;
	handleLinkClick?: (link: unknown) => void;
}

const LaunchpadWidgetWrapper: React.FC<LaunchpadWidgetWrapperProps> = React.memo((props) => {
	const {
		widgetDetails,
		editMode = true,
		headerIcon = '',
		iconColor,
		children,
		isLoading = false,
		t = () => '',
		isTitleBlock = true,
		wrapperClassNames = '',
		hideHeader = false,
		headerText = '',
		headerDescription = '',
		isBannerWidget = false,
		isOtherLink = false,
		editWidgetHeight = null,
		isHeaderWidget = false,
		setIsInPreviewMode = () => {},
		setWidgetCustomizationData = () => {},
		setHeaderImageUrl = () => {},
		widgetCustomizationData = {},
		headerWidgetImageUrl = '',
		headerWidgetTitle = '',
		headerWidgetDescription = '',
		widgetFinishStatus = {},
		setWidgetFinishStatus = () => {},
		bannerConfig = {},
		editModalInputFields = {},
		setEditModalInputFields = () => {},
		defaultWidgetStyles = {},
		bannerImageUrl = '',
		setBannerImageUrl = () => {},
		setSnackbar = () => {},
		widgetDrawerThemeSettings = {},
		selectLaunchpadSettingsData,
		handleLinkClick = () => {},
	} = props;

	const [showEditModal, setShowEditModal] = useState(false);

	useEffect(() => {
		const widgetInputDefaults: Record<string, unknown> = {
			'widget-header': {
				headerImage: headerWidgetImageUrl,
				headerTitle: headerWidgetTitle,
				headerSubtitle: headerWidgetDescription,
			},
			'widget-banner': {
				bannerImage: bannerConfig?.bannerImage,
				bannerHoverText: bannerConfig?.bannerHoverText,
				bannerHoverUrl: bannerConfig?.bannerHoverUrl,
			},
			'widget-marketing': defaultWidgetStyles,
			'widget-crm': defaultWidgetStyles,
			'widget-listings': defaultWidgetStyles,
			'widget-websites': defaultWidgetStyles,
			'widget-financial': defaultWidgetStyles,
			'widget-otherLinks': defaultWidgetStyles,
			'widget-adsAndLeads': defaultWidgetStyles,
			'widget-listingsSmc': defaultWidgetStyles,
			'widget-landingPages': defaultWidgetStyles,
		};
		const widgetId = widgetDetails?.id;

		if (widgetId && widgetInputDefaults[widgetId]) {
			setEditModalInputFields((prev) => ({
				...prev,
				[widgetId]: widgetInputDefaults[widgetId],
			}));
		}
	}, [
		widgetDetails?.id,
		headerWidgetImageUrl,
		headerWidgetTitle,
		headerWidgetDescription,
		bannerConfig?.bannerImage,
		bannerConfig?.bannerHoverText,
		bannerConfig?.bannerHoverUrl,
		defaultWidgetStyles,
		setEditModalInputFields,
	]);

	const actionItems = [
		{
			eventKey: 'customize',
			label: 'Customize',
			icon: <BsPencilFill className="bs-gray-700" />,
		},
	];

	const handleWidgetEditCloseModal = (widgetId: string, type?: string) => {
		if (type === 'finish') {
			setShowEditModal(false);
			return;
		}

		const finishedWidgetMappings: Record<string, unknown> = {
			'widget-header': {
				headerImage: widgetCustomizationData?.headerImage,
				headerTitle: widgetCustomizationData?.headerTitle,
				headerSubtitle: widgetCustomizationData?.headerTitleDescription,
			},
			'widget-banner': widgetCustomizationData,
			'widget-marketing': widgetCustomizationData,
			'widget-crm': widgetCustomizationData,
			'widget-listings': widgetCustomizationData,
			'widget-websites': widgetCustomizationData,
			'widget-financial': widgetCustomizationData,
			'widget-otherLinks': widgetCustomizationData,
			'widget-adsAndLeads': widgetCustomizationData,
			'widget-listingsSmc': widgetCustomizationData,
			'widget-landingPages': widgetCustomizationData,
		};

		const defaultWidgetMappings: Record<string, unknown> = {
			'widget-header': {
				headerImage: headerWidgetImageUrl,
				headerTitle: headerWidgetTitle,
				headerSubtitle: headerWidgetDescription,
			},
			'widget-banner': bannerConfig,
			'widget-marketing': defaultWidgetStyles,
			'widget-crm': defaultWidgetStyles,
			'widget-listings': defaultWidgetStyles,
			'widget-websites': defaultWidgetStyles,
			'widget-financial': defaultWidgetStyles,
			'widget-otherLinks': defaultWidgetStyles,
			'widget-adsAndLeads': defaultWidgetStyles,
			'widget-listingsSmc': defaultWidgetStyles,
			'widget-landingPages': defaultWidgetStyles,
		};

		const mappings = widgetFinishStatus[widgetId]
			? finishedWidgetMappings
			: defaultWidgetMappings;

		setEditModalInputFields((prevState) => ({
			...prevState,
			[widgetId]: mappings[widgetId],
		}));

		setShowEditModal(false);
	};

	return (
		<div
			className={`layout-grid rounded-2 ${widgetDetails?.id === 'widget-otherLinks' ? 'overflow-auto' : ''} ${
				isHeaderWidget
					? 'header-widget'
					: isBannerWidget
						? `p-0 ${isLoading ? 'border' : ''}`
						: `p-3 ${editMode ? '' : 'border'}`
			}  h-100 ${isTitleBlock ? '' : 'no-title-block'} ${wrapperClassNames && wrapperClassNames}`}
			style={{ backgroundColor: isOtherLink ? '#F8F8F8' : undefined }}
		>
			{editMode && (
				<div className="ms-auto action-block position-absolute end-0 top-0 mt-3 me-3">
					<BayDropdown
						alignment="end"
						items={actionItems.map((item) => ({
							eventKey: item.eventKey,
							label: (
								<div className=" dropdown-label d-flex align-items-center">
									{item.icon && <span className="me-2">{item.icon}</span>}
									{item.label}
								</div>
							),
							onClick: () => {
								if (item.eventKey === 'customize') {
									setShowEditModal(true);
								}
							},
						}))}
					>
						<div className="bay-icon-editable text-blue">
							<BsThreeDotsVertical size={16} />
						</div>
					</BayDropdown>
				</div>
			)}

			{showEditModal && (
				<EditWidget
					{...(
						{
							bannerConfig,
							bannerImageUrl,
							defaultWidgetStyles,
							editModalInputFields,
							editWidgetHeight,
							handleLinkClick,
							headerWidgetDescription,
							headerWidgetImageUrl,
							headerWidgetTitle,
							selectLaunchpadSettingsData,
							setBannerImageUrl,
							setEditModalInputFields,
							setHeaderImageUrl,
							setIsInPreviewMode,
							setSnackbar,
							setWidgetCustomizationData,
							setWidgetFinishStatus,
							show: showEditModal,
							t,
							widgetCustomizationData,
							widgetDetails,
							widgetDrawerThemeSettings,
							widgetFinishStatus,
							onHide: handleWidgetEditCloseModal,
						} as any
					)}
				/>
			)}

			{!hideHeader && (
				<div
					className={`d-flex align-items-start gap-4 mb-3 ${`${editMode ? 'opacity-50 pe-none' : ''}`}`}
				>
					<div
						className="header-icon p-2 rounded d-flex align-items-center justify-content-center flex-shrink-0"
						style={{
							backgroundColor: iconColor,
						}}
					>
						{headerIcon}
					</div>

					<div className="d-flex flex-column gap-1 overflow-hidden">
						<h3 className="fs-5 fw-semibold m-0 text-nowrap text-truncate">
							{headerText}
						</h3>
						<div className="fs-6 text-secondary">
							<TruncatedDescription
								eventName={headerDescription}
								style="multiline-ellipsis-desc"
								type="description"
							/>
						</div>
					</div>
				</div>
			)}

			<div
				className={`${editMode ? 'opacity-50 pe-none cursor-grabbing' : 'h-100'}`}
			>
				{children}
			</div>
		</div>
	);
});

export default LaunchpadWidgetWrapper;
